<template>
    <div class="task-filter-toolbar-wrapper">
    <div class="task-filter-toolbar">
        <!-- Status (multi-select) -->
        <v-menu v-if="showStatus" offset-y :close-on-content-click="false">
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-format-list-checks</v-icon>
                            Status
                            <v-avatar v-if="status.length" size="16" color="primary" class="ml-1 white--text filter-pill-count">
                                {{ status.length }}
                            </v-avatar>
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Filter by task status</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in statusOptions" :key="opt.id" @click="toggleMulti('status', opt.id)">
                    <v-list-item-action class="mr-2">
                        <v-checkbox :input-value="status.includes(opt.id)" readonly dense hide-details class="ma-0 pa-0" />
                    </v-list-item-action>
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Company -->
        <v-menu offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-domain</v-icon>
                            {{ selectedLabel(companyOptions, company) || 'Company' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Filter by company</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in companyOptions" :key="opt.id ?? 'all'" @click="$emit('update:company', opt.id)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Priority -->
        <v-menu offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-flag-outline</v-icon>
                            {{ selectedLabel(priorityOptions, priority) || 'Priority' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Filter by priority</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in priorityOptions" :key="opt.id ?? 'all'" @click="$emit('update:priority', opt.id)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Assignee -->
        <v-menu v-if="showAssignee" offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-account-outline</v-icon>
                            {{ selectedLabel(assigneeOptions, assignee) || 'Assignee' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Filter by assignee</span>
                </v-tooltip>
            </template>
            <v-list dense class="filter-menu-scroll">
                <v-list-item @click="$emit('update:assignee', null)">
                    <v-list-item-title>Anyone</v-list-item-title>
                </v-list-item>
                <v-list-item v-for="opt in assigneeOptions" :key="opt.id" @click="$emit('update:assignee', opt.id)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Due Date -->
        <v-menu offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-calendar</v-icon>
                            {{ selectedLabel(dueDateOptions, dueDate, 'value') || 'Due Date' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Filter by due date</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in dueDateOptions" :key="opt.value ?? 'all'" @click="$emit('update:due-date', opt.value)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Group -->
        <v-menu v-if="showGroupBy" offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-format-list-group</v-icon>
                            {{ selectedLabel(groupByOptions, groupBy, 'value') || 'Group' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Group tasks by</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in groupByOptions" :key="opt.value" @click="$emit('update:group-by', opt.value)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <!-- Sort -->
        <v-menu v-if="showGroupBy" offset-y>
            <template v-slot:activator="{ on: menu, attrs }">
                <v-tooltip bottom>
                    <template v-slot:activator="{ on: tooltip }">
                        <v-btn outlined rounded small v-bind="attrs" v-on="{ ...menu, ...tooltip }" class="filter-pill" :disabled="disabled">
                            <v-icon x-small left>mdi-sort</v-icon>
                            {{ selectedLabel(sortByOptions, sortBy, 'value') || 'Sort' }}
                            <v-icon small right>mdi-menu-down</v-icon>
                        </v-btn>
                    </template>
                    <span>Sort tasks by</span>
                </v-tooltip>
            </template>
            <v-list dense>
                <v-list-item v-for="opt in sortByOptions" :key="opt.value" @click="$emit('update:sort-by', opt.value)">
                    <v-list-item-title>{{ opt.name }}</v-list-item-title>
                </v-list-item>
            </v-list>
        </v-menu>

        <v-btn text small color="primary" class="text-none flex-shrink-0" @click="$emit('clear')">
            Clear filters
        </v-btn>
    </div>
    </div>
</template>

<script>
// Presentational filter toolbar shared by My Work's three sub-tabs. Owns no
// filter state itself — every value is passed in and changes are emitted
// back up, so DashboardComponent/MyWorkComponent's existing per-tab filter
// state (personalFilters/assignedFilters/createdForOthersFilters + the three
// Vuex status-filter arrays) stays exactly where it already lives, untouched.
export default {
    name: 'TaskFilterToolbar',
    props: {
        showStatus: { type: Boolean, default: true },
        status: { type: Array, default: () => [] },
        statusOptions: { type: Array, default: () => [] },
        company: { type: [Number, String], default: null },
        companyOptions: { type: Array, default: () => [] },
        priority: { type: [Number, String], default: null },
        priorityOptions: { type: Array, default: () => [] },
        showAssignee: { type: Boolean, default: false },
        assignee: { type: [Number, String], default: null },
        assigneeOptions: { type: Array, default: () => [] },
        dueDate: { type: String, default: null },
        dueDateOptions: { type: Array, default: () => [] },
        showGroupBy: { type: Boolean, default: false },
        groupBy: { type: String, default: 'status' },
        groupByOptions: { type: Array, default: () => [] },
        sortBy: { type: String, default: 'none' },
        sortByOptions: { type: Array, default: () => [] },
        disabled: { type: Boolean, default: false },
    },
    methods: {
        selectedLabel(options, value, valueKey = 'id') {
            if (value == null) return null;
            const match = options.find(o => o[valueKey] === value);
            return match ? match.name : null;
        },
        toggleMulti(prop, id) {
            const current = this[prop] || [];
            const next = current.includes(id) ? current.filter(v => v !== id) : [...current, id];
            this.$emit(`update:${prop}`, next);
        },
    },
};
</script>

<style scoped>
.task-filter-toolbar-wrapper {
    display: flex;
    flex-direction: column;
    gap: 4px;
    min-width: 0;
}

.task-filter-toolbar {
    display: flex;
    align-items: center;
    flex-wrap: nowrap;
    gap: 8px;
}

.filter-pill {
    text-transform: none;
    font-weight: 500;
    color: #444;
    border-color: #dcdcdc;
    flex-shrink: 0;
}

.filter-pill-count {
    font-size: 10px;
}

.filter-menu-scroll {
    max-height: 320px;
    overflow-y: auto;
}
</style>
